import React from 'react';
import toastr from 'react-hot-toast';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { useWorkspaceContext } from 'hooks/common/useWorkspaceContext';
import {
  generateFilter,
  generateIncludeQuery,
  generatePaginationQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { fetchWrapper } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import type { JoinedMemory, Memory, MemoryResponse } from 'types/Memory';
import type { DropdownOption } from 'types/common/DropdownOption';
import { createJoinedMemories } from 'helpers/spark';
import { SPARK_RESOURCE_TYPES } from 'constants/common';

const getMemory = (
  currentWorkspaceID: string | null | undefined,
  filterUrl: string | undefined,
  pageParam: any
) => {
  if (!currentWorkspaceID) {
    Promise.reject('workspace ID not  found');
    return;
  }

  if (filterUrl) {
    const url = `${filterUrl}&${generatePaginationQuery({
      pageNumber: pageParam,
      pageSize: 10,
    })}&${generateIncludeQuery(['memoryFavourites'])}`;
    return fetchWrapper<MemoryResponse>({
      url: url,
      method: 'GET',
    });
  }
  // fallback to base memory url
  const queryArray = [
    generateFilter('workspaceID', currentWorkspaceID),
    generatePaginationQuery({
      pageNumber: pageParam,
      pageSize: 10,
    }),
  ];
  const url = generateURL(SPARK_RESOURCE_TYPES.MEMORIES, queryArray);

  return fetchWrapper<MemoryResponse>({
    url: url,
    method: 'GET',
  });
};

type MemoryQueryFilters = {
  url?: string;
};

export function useInfiniteMemoriesQuery(
  memoryQueryFilters?: MemoryQueryFilters,
  enabled = true
) {
  const { url } = memoryQueryFilters || {};
  const { currentWorkspaceID } = useWorkspaceContext();
  const queryClient = useQueryClient();

  const queryResult = useInfiniteQuery(
    ['memories', url],
    ({ pageParam = 1 }) => getMemory(currentWorkspaceID, url, pageParam),
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.data?.length ? allPages?.length + 1 : undefined;
      },
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Knowledge Sources: ${error.message}`);
        }
      },
      enabled,
      ...CACHE_AND_STALE_TIME,
    }
  );
  React.useEffect(() => {
    return () => {
      queryClient.cancelQueries(['memories', url]);
    };
  }, [queryClient, url]);

  const { data: memoryResponse } = queryResult;

  const joinedMemories = memoryResponse?.pages.reduce<JoinedMemory[]>(
    (acc, page) => {
      if (!page?.data) {
        return acc;
      }
      const joinedMemory = createJoinedMemories(page);
      return [...acc, ...joinedMemory];
    },
    []
  );
  const memoryDropdownOptions = React.useMemo(() => {
    return joinedMemories?.reduce<DropdownOption[]>((acc, curr) => {
      const option = { label: curr.attributes.name, value: curr.id };

      return [...acc, option];
    }, []);
  }, [joinedMemories]);

  const memoryByIdMap = React.useMemo(() => {
    return joinedMemories?.reduce<Record<string, Memory>>((acc, curr) => {
      return { ...acc, [curr.id]: curr };
    }, {});
  }, [joinedMemories]);

  return {
    memoryDropdownOptions,
    memoryByIdMap,
    joinedMemories,
    ...queryResult,
  };
}
